import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { getMergeGroups } from "~/services/antecipebackoffice/packageMoves";
import { getCompanies } from "~/services/companies";
import { useNotify } from "~/shared/ui/feedback/SnackbarProvider";
import { getApiErrorMessage, isCanceledRequest } from "~/utils/apiError";
import { brl, joinIdsPt, plural } from "~/utils/format";
import { buildSections, canJoin, getAnchor, getLockSummary, indexRows } from "~/utils/mergeSelection";
import { DEFAULT_GROUP_FILTERS, GROUP_LIMITS, groupFiltersToApi, groupFiltersToSearch, parseGroupFilters } from "~/utils/packageFilters";
import CompanySuggestionCards from "./CompanySuggestionCards";
import JoinPackagesDialog from "./JoinPackagesDialog";
import PackageDetailDrawer from "./PackageDetailDrawer";
import PackageGroupSection from "./PackageGroupSection";
import PackageGroupsFilterBar from "./PackageGroupsFilterBar";
import { useAuth } from "~/auth/AuthProvider";
import ReadOnlyChip from "~/components/ReadOnlyChip";
import PageHeader from "~/shared/ui/PageHeader";

export default function PackageGroupsView() {
  const { permissions } = useAuth();
  const canWrite = permissions.backofficeWrite;
  const canReadBackoffice = permissions.backofficeRead;
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseGroupFilters(searchParams), [searchParams]);
  const apiKey = JSON.stringify(groupFiltersToApi(filters));
  const [state, setState] = useState({ status: "loading", data: null, error: null });
  const [reloadToken, setReloadToken] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [collapsed, setCollapsed] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [joinOpen, setJoinOpen] = useState(false);
  const notify = useNotify();

  // GET antecipebackoffice/companies exige Leitura+ no BE: sem ela, nem chama (o filtro fica sem empresas).
  useEffect(() => {
    if (!canReadBackoffice) return;
    getCompanies({ includeInactive: filters.includeInactive }).then(setCompanies).catch(() => setCompanies([]));
  }, [canReadBackoffice, filters.includeInactive]);
  const inactiveCompanyIds = useMemo(() => new Set(companies.filter((c) => c.active === false).map((c) => c.id)), [companies]);

  useEffect(() => {
    const controller = new AbortController();
    // Toda nova busca (filtro, página, "Tentar de novo" ou depois de juntar) zera a seleção: âncora velha nunca fica.
    setSelectedIds([]);
    setState((s) => ({ ...s, status: "loading", error: null }));
    getMergeGroups(JSON.parse(apiKey), { signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted) setState({ status: "ok", data, error: null });
      })
      .catch((e) => {
        if (controller.signal.aborted || isCanceledRequest(e)) return;
        setState({ status: "error", data: null, error: getApiErrorMessage(e, "O servidor do Antecipe não respondeu.") });
      });
    return () => controller.abort();
  }, [apiKey, reloadToken]);

  const update = (patch) => setSearchParams(groupFiltersToSearch({ ...filters, ...patch }));

  // Depois de juntar na última página ela pode deixar de existir: vai para a última página válida (mesma regra da tela da empresa).
  useEffect(() => {
    if (state.status !== "ok" || state.data.groups.length > 0) return;
    const lastPage = Math.max(1, Math.ceil((state.data.total || 0) / filters.limit));
    if (state.data.total > 0 && filters.page > lastPage) update({ page: lastPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, filters.page, filters.limit]);
  const sections = useMemo(() => buildSections(state.data?.groups || []), [state.data]);
  const rowsById = useMemo(() => indexRows(sections), [sections]);
  const selectedRows = useMemo(() => selectedIds.map((id) => rowsById.get(id)).filter(Boolean), [selectedIds, rowsById]);
  const anchor = getAnchor(selectedIds, rowsById);
  const joinable = canJoin(selectedIds, rowsById);
  const doctorSection = sections.find((s) => String(s.doctor.id) === filters.doctorId);
  const doctorLabel = doctorSection ? `${doctorSection.doctor.name} · CRM ${doctorSection.doctor.crm}/${doctorSection.doctor.crmUf}` : "";

  const onSelectionChange = (sectionKey, model) => {
    const section = sections.find((s) => s.key === sectionKey);
    const sectionIds = section.rows.map((r) => r.id);
    setSelectedIds((current) => {
      const kept = current.filter((id) => !sectionIds.includes(id) || model.includes(id));
      const added = model.filter((id) => !current.includes(id));
      return [...kept, ...added];
    });
  };

  const onMerged = (result) => {
    const gross = selectedRows.reduce((acc, row) => acc + Number(row.grossTotal || 0), 0);
    setJoinOpen(false);
    setSelectedIds([]);
    notify({
      severity: "success",
      durationMs: 6000,
      message: result.alreadyMerged
        ? `Esses pacotes já estavam agrupados no #${result.id}.`
        : `Pacote #${result.id} criado${result.movedVacancies != null ? ` com ${plural(result.movedVacancies, "plantão", "plantões")}` : ""} (${brl(gross)}). ${joinIdsPt(result.mergedFrom)} agora ${
            result.mergedFrom.length === 1 ? "está" : "estão"
          } como “Agrupado no #${result.id}”.`,
    });
    setReloadToken((t) => t + 1);
  };

  // Remonta a barra: descarta texto ainda em debounce (com a URL já no padrão, nada mais o limparia).
  const [filterBarKey, setFilterBarKey] = useState(0);
  const resetFilters = () => {
    setFilterBarKey((k) => k + 1);
    setSearchParams(groupFiltersToSearch(DEFAULT_GROUP_FILTERS));
  };
  const total = state.data?.total || 0;
  const firstShown = total ? (filters.page - 1) * filters.limit + 1 : 0;
  const lastShown = Math.min(filters.page * filters.limit, total);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Pacotes"
        description="Todos os pacotes, inclusive os que ainda estão com a empresa médica. Os filtros ficam no endereço da página: dá para recarregar ou mandar o link."
        actions={!canWrite ? <ReadOnlyChip /> : null}
      />
      <PackageGroupsFilterBar key={filterBarKey} value={filters} companies={companies} doctorLabel={doctorLabel} onChange={update} onClear={resetFilters} />

      {state.status === "loading" && (
        <Box role="status" aria-label="Carregando pacotes">
          {[0, 1].map((i) => (
            <Skeleton key={i} variant="rectangular" height={120} sx={{ mb: 1.5, borderRadius: 1 }} />
          ))}
        </Box>
      )}

      {state.status === "error" && (
        <Box sx={{ textAlign: "center", p: 4, border: 1, borderColor: "divider", borderRadius: 1 }}>
          <Typography aria-hidden="true" sx={{ fontSize: 28 }}>⚠</Typography>
          <Typography component="h4" variant="h6">Não conseguimos carregar os pacotes</Typography>
          <Typography sx={{ mb: 2 }}>{state.error} Seus filtros foram mantidos.</Typography>
          <Button variant="contained" onClick={() => setReloadToken((t) => t + 1)}>Tentar de novo</Button>
        </Box>
      )}

      {state.status === "ok" && state.data.groups.length === 0 && (
        <Box sx={{ textAlign: "center", p: 4, border: 1, borderColor: "divider", borderRadius: 1 }}>
          <Typography aria-hidden="true" sx={{ fontSize: 28 }}>🔍</Typography>
          <Typography component="h4" variant="h6">Nenhum pacote com esses filtros</Typography>
          <Typography sx={{ mb: 2 }}>Tente outro período ou tire o filtro “Candidatos a agrupar”.</Typography>
          <Button variant="outlined" onClick={resetFilters}>Limpar filtros</Button>
        </Box>
      )}

      {state.status === "ok" && state.data.groups.length > 0 && (
        <>
          <CompanySuggestionCards
            summary={state.data.companySummary || []}
            activeCompanyId={filters.companyId}
            onReview={(companyId) => update({ companyId: String(companyId), onlyCandidates: true, page: 1 })}
          />
          {canWrite && (
            <Box
              role="region"
              aria-label="Seleção"
              sx={{ position: "sticky", top: 0, zIndex: 2, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, p: 1, mb: 1, bgcolor: "#eceff1", borderRadius: 1 }}
            >
              <span><b>{selectedIds.length}</b> pacotes selecionados</span>
              <Button variant="contained" color="secondary" disabled={!joinable} onClick={() => setJoinOpen(true)}>
                {joinable ? `⧉ Juntar ${selectedIds.length} pacotes` : "⧉ Juntar selecionados"}
              </Button>
              <Button onClick={() => setSelectedIds([])}>Limpar seleção</Button>
              {selectedIds.length < 2 && (
                <Typography variant="body2" color="text.secondary">Marque 2 ou mais pacotes do mesmo médico e da mesma empresa.</Typography>
              )}
              {anchor && <Alert severity="info" icon={false} sx={{ width: "100%" }}>{getLockSummary(anchor)}</Alert>}
            </Box>
          )}
          {sections.map((section) => (
            <PackageGroupSection
              key={section.key}
              section={section}
              selectedIds={selectedIds}
              anchor={anchor}
              collapsed={collapsed.includes(section.key)}
              onToggleCollapse={() =>
                setCollapsed((c) => (c.includes(section.key) ? c.filter((k) => k !== section.key) : [...c, section.key]))
              }
              onSelectionChange={onSelectionChange}
              onPick={(ids) => setSelectedIds(ids)}
              onOpen={setOpenId}
              selectable={canWrite}
              inactiveCompanyIds={inactiveCompanyIds}
            />
          ))}
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "flex-end", gap: 2, mt: 1 }}>
            <TextField
              select size="small" label="Linhas por página" value={filters.limit} sx={{ width: 150 }}
              onChange={(e) => update({ limit: Number(e.target.value), page: 1 })}
            >
              {GROUP_LIMITS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            </TextField>
            <span>{firstShown}–{lastShown} de {plural(total, "grupo", "grupos")}</span>
            <Pagination
              color="primary"
              page={filters.page}
              count={Math.max(1, Math.ceil(total / filters.limit))}
              onChange={(event, page) => update({ page })}
            />
          </Box>
        </>
      )}

      {/* Montado só enquanto aberto: o estado interno do modal zera a cada abertura. */}
      {joinOpen && canWrite && <JoinPackagesDialog open rows={selectedRows} onCancel={() => setJoinOpen(false)} onMerged={onMerged} />}
      <PackageDetailDrawer
        key={openId ?? "closed"}
        packageId={openId}
        row={openId ? rowsById.get(openId) || null : null}
        onClose={() => setOpenId(null)}
        onOpenPackage={setOpenId}
      />
    </Box>
  );
}
